import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { camelCase } from 'lodash-es';

import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  ListItem,
  Text,
  UnorderedList,
} from '@chakra-ui/react';
import { SortIcon } from '@udacity/chakra-uds-icons';
import { headerHeight } from '@udacity/udacity-header-v2';

import { Link } from '~/components/link';

import { CaseStudyProcessStep } from '../models/case-study-content';

export function ProcessStepsTableOfContents({ processSteps }: { processSteps: CaseStudyProcessStep[] }) {
  const router = useRouter();
  const [currentRoute, setCurrentRoute] = useState('');

  useEffect(() => {
    if (router.isReady) {
      setCurrentRoute(router.asPath.split('#')?.[1]);
    }
  }, [router]);

  const tableOfContents = useMemo(
    () =>
      processSteps.map((processStep) => {
        const href = camelCase(`caseStudy${processStep.title}`);
        const isActiveHref = currentRoute === href;

        return {
          title: processStep.title,
          href,
          isActiveHref,
        };
      }),
    [currentRoute, processSteps]
  );

  return (
    <>
      <Box display={{ base: 'block', lg: 'none' }}>
        <MobileTableOfContents tableOfContents={tableOfContents} />
      </Box>
      <Box display={{ base: 'none', lg: 'block' }} position='sticky' top={`calc(${headerHeight} + 16px)`}>
        <DesktopTableOfContents tableOfContents={tableOfContents} />
      </Box>
    </>
  );
}

function MobileTableOfContents({
  tableOfContents,
}: {
  tableOfContents: {
    title: string;
    href: string;
    isActiveHref: boolean;
  }[];
}) {
  return (
    <Accordion allowToggle>
      <AccordionItem borderRadius='8px'>
        <AccordionButton
          _expanded={{ borderTopRadius: '8px', borderBottomRadius: '0px' }}
          background='gray.100'
          borderRadius='8px'
          padding='8px 16px'
        >
          <Text fontSize='12px' fontWeight='600' marginRight='12px' textTransform='uppercase'>
            Table Of Contents
          </Text>

          <SortIcon height='32px' marginLeft='auto' width='32px' />
        </AccordionButton>
        <AccordionPanel borderBottomRadius='8px' padding='16px 0px'>
          <UnorderedList display='flex' flexDirection='column' gap='8px' margin={0}>
            {tableOfContents.map((contentItem) => (
              <ListItem key={contentItem.href} listStyleType='none'>
                <Link
                  display='block'
                  href={`#${contentItem.href}`}
                  padding='8px 16px'
                  textDecoration='none'
                  sx={{
                    backgroundColor: contentItem.isActiveHref ? 'accent.sky.100' : 'unset',
                    color: contentItem.isActiveHref ? 'blue.500' : 'blue.700',
                    _visited: contentItem.isActiveHref ? 'blue.500' : 'blue.700',

                    _hover: {
                      color: 'blue.500',
                      backgroundColor: 'accent.sky.100',
                    },
                  }}
                >
                  {contentItem.title}
                </Link>
              </ListItem>
            ))}
          </UnorderedList>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
}

function DesktopTableOfContents({
  tableOfContents,
}: {
  tableOfContents: {
    title: string;
    href: string;
    isActiveHref: boolean;
  }[];
}) {
  return (
    <Box as='nav' backgroundColor='gray.50' borderRadius='8px' height='fit-content' padding='20px 8px'>
      <UnorderedList margin={0}>
        <ListItem listStyleType='none'>
          <Text color='gray.500' fontSize='12px' fontWeight='700' paddingX='16px' textTransform='uppercase'>
            CONTENTS
          </Text>

          <UnorderedList display='flex' flexDirection='column' gap='8px' margin={0} marginTop='8px'>
            {tableOfContents.map((contentItem) => (
              <ListItem key={contentItem.href} listStyleType='none'>
                <Link
                  display='block'
                  href={`#${contentItem.href}`}
                  padding='8px 16px'
                  textDecoration='none'
                  sx={{
                    backgroundColor: contentItem.isActiveHref ? 'accent.sky.100' : 'unset',
                    color: contentItem.isActiveHref ? 'blue.500' : 'blue.700',
                    _visited: contentItem.isActiveHref ? 'blue.500' : 'blue.700',

                    _hover: {
                      color: 'blue.500',
                      backgroundColor: 'accent.sky.100',
                    },
                  }}
                >
                  {contentItem.title}
                </Link>
              </ListItem>
            ))}
          </UnorderedList>
        </ListItem>
      </UnorderedList>
    </Box>
  );
}
